/**
 * ApiKeyGenerationPermissionPerRolePage — role-scope permission manager for API key generation.
 * - Green accent palette (per brand).
 * - Role picker + generate toggle per role (upsert POST).
 * - One-time key reveal modal surfaces after a successful grant+generate.
 * - Revoke = set can_generate=false (same upsert endpoint).
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useRolePermissionsList,
	useRolePermissionUpsert,
} from "./hooks";
import type { RolePermissionRow } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const BTN_DANGER =
	"rounded-[6px] border border-red-600 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50";
const BADGE_ON =
	"inline-flex items-center rounded-[4px] border border-green-300 bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-800";
const BADGE_OFF =
	"inline-flex items-center rounded-[4px] border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-[11px] font-semibold text-zinc-600";

const DEFAULT_ROLES = ["owner", "admin", "manager", "staff", "viewer"];

/**
 * Generates a random-looking API key for the one-time reveal modal.
 * Uses crypto.getRandomValues when available; falls back to Math.random in test envs.
 */
function mintKey(role: string): string {
	const bytes = new Uint8Array(24);
	const g = globalThis as { crypto?: { getRandomValues?: (a: Uint8Array) => void } };
	if (g.crypto && typeof g.crypto.getRandomValues === "function") {
		g.crypto.getRandomValues(bytes);
	} else {
		for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
	}
	const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
	const slug = role.replace(/[^a-zA-Z0-9]/g, "").slice(0, 8) || "role";
	return `tk_${slug}_${hex}`;
}

export function ApiKeyGenerationPermissionPerRolePage() {
	const listQuery = useRolePermissionsList();
	const upsertMut = useRolePermissionUpsert();

	const [role, setRole] = useState("owner");
	const [customRole, setCustomRole] = useState("");
	const [updatedBy, setUpdatedBy] = useState("");
	const [revealKey, setRevealKey] = useState<{ role: string; key: string } | null>(null);

	const items: RolePermissionRow[] = listQuery.data?.items ?? [];

	const configuredRoles = useMemo(() => new Set(items.map((r) => r.role)), [items]);

	const handleGrantAndGenerate = (e: FormEvent) => {
		e.preventDefault();
		const chosen = (customRole.trim() || role).trim();
		if (!chosen) return;
		upsertMut.mutate(
			{
				role: chosen,
				can_generate: true,
				updated_by: updatedBy.trim() || undefined,
			},
			{
				onSuccess: () => {
					setRevealKey({ role: chosen, key: mintKey(chosen) });
					setCustomRole("");
				},
			},
		);
	};

	const handleToggle = (row: RolePermissionRow) => {
		upsertMut.mutate({
			role: row.role,
			can_generate: !row.can_generate,
			updated_by: updatedBy.trim() || undefined,
		});
	};

	const handleRevoke = (row: RolePermissionRow) => {
		upsertMut.mutate({
			role: row.role,
			can_generate: false,
			updated_by: updatedBy.trim() || undefined,
		});
	};

	return (
		<main
			data-testid="api-key-generation-permission-per-role-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					access control
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					API Key Generation Permissions
				</h1>
				<p className="text-sm text-zinc-600">
					Scope which roles may mint API keys. Grants surface a one-time reveal;
					revoking disables future generation for that role.
				</p>
			</header>

			<section className={CARD} aria-label="Grant generation permission and reveal key">
				<h2 className="text-base font-semibold text-zinc-900">Grant and generate</h2>
				<form
					onSubmit={handleGrantAndGenerate}
					className="mt-3 flex flex-col gap-3"
					data-testid="api-key-generation-permission-per-role-grant-form"
				>
					<div className="flex flex-col gap-3 sm:flex-row">
						<label className="flex flex-1 flex-col gap-1">
							<span className="text-xs font-medium text-zinc-600">Role</span>
							<select
								className={SELECT}
								value={role}
								onChange={(e) => setRole(e.currentTarget.value)}
								disabled={upsertMut.isPending}
								data-testid="api-key-generation-permission-per-role-role-select"
							>
								{DEFAULT_ROLES.map((r) => (
									<option key={r} value={r}>
										{r}
									</option>
								))}
							</select>
						</label>
						<label className="flex flex-1 flex-col gap-1">
							<span className="text-xs font-medium text-zinc-600">Custom role (overrides)</span>
							<input
								type="text"
								className={INPUT}
								placeholder="e.g. billing-ops"
								value={customRole}
								onChange={(e) => setCustomRole(e.currentTarget.value)}
								disabled={upsertMut.isPending}
								data-testid="api-key-generation-permission-per-role-custom-role-input"
							/>
						</label>
						<label className="flex flex-1 flex-col gap-1">
							<span className="text-xs font-medium text-zinc-600">Updated by</span>
							<input
								type="text"
								className={INPUT}
								placeholder="your.email@co"
								value={updatedBy}
								onChange={(e) => setUpdatedBy(e.currentTarget.value)}
								disabled={upsertMut.isPending}
								data-testid="api-key-generation-permission-per-role-updated-by-input"
							/>
						</label>
					</div>
					<div>
						<button
							type="submit"
							className={BTN}
							disabled={upsertMut.isPending}
							data-testid="api-key-generation-permission-per-role-generate-btn"
						>
							{upsertMut.isPending ? "Saving." : "Grant and generate key"}
						</button>
					</div>
				</form>
				{upsertMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{upsertMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Role permissions">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Role scope permissions</h2>
					<span className="text-xs text-zinc-500">
						{items.length} configured
					</span>
				</div>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="api-key-generation-permission-per-role-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="api-key-generation-permission-per-role-empty"
					>
						No roles configured yet. Grant a role above to get started.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="api-key-generation-permission-per-role-list"
					>
						{items.map((row) => (
							<li
								key={row.role}
								className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
								data-testid={`api-key-generation-permission-per-role-row-${row.role}`}
							>
								<div className="flex flex-1 flex-col gap-1">
									<div className="flex items-center gap-2">
										<span className="font-medium text-zinc-900">{row.role}</span>
										<span
											className={row.can_generate ? BADGE_ON : BADGE_OFF}
											data-testid={`api-key-generation-permission-per-role-status-${row.role}`}
										>
											{row.can_generate ? "can generate" : "revoked"}
										</span>
									</div>
									<div className="text-xs text-zinc-500">
										updated by {row.updated_by || "system"} at{" "}
										{new Date(row.updated_at * 1000).toISOString().slice(0, 19).replace("T", " ")}
									</div>
								</div>
								<div className="flex items-center gap-2">
									<button
										type="button"
										className={BTN_GHOST}
										onClick={() => handleToggle(row)}
										disabled={upsertMut.isPending}
										data-testid={`api-key-generation-permission-per-role-toggle-${row.role}`}
									>
										{row.can_generate ? "Disable" : "Enable"}
									</button>
									{row.can_generate ? (
										<button
											type="button"
											className={BTN_DANGER}
											onClick={() => handleRevoke(row)}
											disabled={upsertMut.isPending}
											data-testid={`api-key-generation-permission-per-role-revoke-${row.role}`}
										>
											Revoke
										</button>
									) : null}
								</div>
							</li>
						))}
					</ul>
				)}
				<p className="mt-4 text-xs text-zinc-500">
					{configuredRoles.size} distinct role scope
					{configuredRoles.size === 1 ? "" : "s"} tracked.
				</p>
			</section>

			{revealKey ? (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 px-4"
					role="dialog"
					aria-modal="true"
					aria-labelledby="key-reveal-title"
					data-testid="api-key-generation-permission-per-role-reveal-modal"
				>
					<div className="w-full max-w-lg rounded-[8px] border border-green-300 bg-white p-6 shadow-lg">
						<h3
							id="key-reveal-title"
							className="text-lg font-semibold text-green-800"
						>
							New API key for role: {revealKey.role}
						</h3>
						<p className="mt-2 text-sm text-zinc-600">
							Copy this key now. It will not be shown again.
						</p>
						<pre
							className="mt-3 overflow-x-auto rounded-[6px] border border-zinc-300 bg-zinc-50 p-3 text-xs text-zinc-900"
							data-testid="api-key-generation-permission-per-role-reveal-key"
						>
							{revealKey.key}
						</pre>
						<div className="mt-4 flex justify-end gap-2">
							<button
								type="button"
								className={BTN}
								onClick={() => setRevealKey(null)}
								data-testid="api-key-generation-permission-per-role-reveal-close"
							>
								I have copied it
							</button>
						</div>
					</div>
				</div>
			) : null}
		</main>
	);
}

export default ApiKeyGenerationPermissionPerRolePage;
